import React from 'react';
import { ShieldCheck, Coins, GitFork, Lock, ArrowRight } from 'lucide-react';

interface SlideProps {
  onNext?: () => void;
}

export const Slide01Vision: React.FC<SlideProps> = ({ onNext }) => {
  return (
    <div className="relative w-full h-full flex flex-col justify-between p-4 sm:p-7 lg:p-10 overflow-y-auto sm:overflow-hidden">
      {/* Background glow & subtle visual elements */}
      <div className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-80 sm:w-[600px] h-80 sm:h-[600px] bg-gold-500/10 dark:bg-gold-500/15 rounded-full blur-[100px] pointer-events-none" />
      <div className="absolute bottom-0 right-0 w-64 sm:w-96 h-64 sm:h-96 bg-blue-600/10 rounded-full blur-[90px] pointer-events-none" />

      {/* Top Header */}
      <div className="relative z-10 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2.5 sm:gap-3">
          <div className="w-8 sm:w-10 h-8 sm:h-10 rounded-xl bg-gold-500 flex items-center justify-center shadow-gold-glow shrink-0">
            <ShieldCheck className="w-5 sm:w-6 h-5 sm:h-6 text-navy-950 stroke-[2.5]" />
          </div>
          <div>
            <h1 className="text-base sm:text-xl font-bold tracking-tight text-slate-900 dark:text-white font-mono">
              WORKOUSE<span className="text-gold-500">.AI</span>
            </h1>
            <p className="text-[10px] sm:text-xs font-mono text-slate-500 dark:text-slate-400">
              Enterprise AI Gateway &amp; Sovereignty Platform
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="px-2.5 sm:px-3 py-1 rounded-full text-[10px] sm:text-xs font-mono font-semibold bg-gold-500/15 text-gold-800 dark:text-gold-400 border border-gold-500/30">
            SEED MEMO
          </span>
        </div>
      </div>

      {/* Center Vision Statement */}
      <div className="relative z-10 my-auto max-w-4xl space-y-3 sm:space-y-5 py-2">
        <div className="inline-flex items-center gap-2 px-2.5 sm:px-3 py-1 rounded-lg bg-slate-200/80 dark:bg-navy-800 border border-slate-300 dark:border-navy-700 text-[11px] sm:text-xs font-mono text-slate-800 dark:text-slate-300">
          <span className="w-2 h-2 rounded-full bg-emerald-500" />
          The Infrastructure Layer for Sovereign Enterprise AI
        </div>

        <h2 className="text-2xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-slate-900 dark:text-white leading-[1.15]">
          Zero-Trust Enterprise AI Sovereignty &amp;{' '}
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-600 via-amber-500 to-yellow-500 dark:from-gold-400 dark:via-amber-400 dark:to-yellow-300">
            In-Memory DLP Vault
          </span>
        </h2>

        <p className="text-sm sm:text-base lg:text-lg text-slate-600 dark:text-slate-300 font-normal leading-relaxed max-w-3xl">
          Empowering enterprises to adopt any LLM (OpenAI, Anthropic, Gemini, DeepSeek, Local Ollama) with mathematical data sovereignty, sub-millisecond RAM token sanitization, and atomic cost guardrails.
        </p>

        {/* 4 Pillars Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2.5 sm:gap-3 pt-2">
          <div className="p-3 sm:p-3.5 rounded-xl bg-white/90 dark:bg-navy-850/80 border border-slate-200 dark:border-navy-700/70 shadow-sm backdrop-blur-sm">
            <div className="flex items-center gap-1.5 text-gold-700 dark:text-gold-400 font-mono text-[11px] sm:text-xs font-bold mb-1">
              <Lock className="w-3.5 h-3.5" />
              <span>01. ZERO LEAKAGE</span>
            </div>
            <p className="text-xs text-slate-600 dark:text-slate-300 font-medium">
              In-memory DLP token vault + HashiCorp Vault secrets isolation.
            </p>
          </div>

          <div className="p-3 sm:p-3.5 rounded-xl bg-white/90 dark:bg-navy-850/80 border border-slate-200 dark:border-navy-700/70 shadow-sm backdrop-blur-sm">
            <div className="flex items-center gap-1.5 text-gold-700 dark:text-gold-400 font-mono text-[11px] sm:text-xs font-bold mb-1">
              <GitFork className="w-3.5 h-3.5" />
              <span>02. V8 SANDBOXES</span>
            </div>
            <p className="text-xs text-slate-600 dark:text-slate-300 font-medium">
              Isolated execution with Two-Phase HITL dry-run diff simulation.
            </p>
          </div>

          <div className="p-3 sm:p-3.5 rounded-xl bg-white/90 dark:bg-navy-850/80 border border-slate-200 dark:border-navy-700/70 shadow-sm backdrop-blur-sm">
            <div className="flex items-center gap-1.5 text-gold-700 dark:text-gold-400 font-mono text-[11px] sm:text-xs font-bold mb-1">
              <Coins className="w-3.5 h-3.5" />
              <span>03. WAP PLUGINS</span>
            </div>
            <p className="text-xs text-slate-600 dark:text-slate-300 font-medium">
              Standardized connector action protocol (Paraşüt, CRM &amp; REST).
            </p>
          </div>

          <div className="p-3 sm:p-3.5 rounded-xl bg-white/90 dark:bg-navy-850/80 border border-slate-200 dark:border-navy-700/70 shadow-sm backdrop-blur-sm">
            <div className="flex items-center gap-1.5 text-gold-700 dark:text-gold-400 font-mono text-[11px] sm:text-xs font-bold mb-1">
              <ShieldCheck className="w-3.5 h-3.5" />
              <span>04. ENTERPRISE GOV</span>
            </div>
            <p className="text-xs text-slate-600 dark:text-slate-300 font-medium">
              USD atomic budget caps, SAML 2.0 / LDAP, and immutable audit logs.
            </p>
          </div>
        </div>
      </div>

      {/* Bottom Action Footer */}
      <div className="relative z-10 flex flex-wrap items-center justify-between gap-2 pt-3 border-t border-slate-200 dark:border-navy-700/60 text-[11px] sm:text-xs font-mono text-slate-500 dark:text-slate-400">
        <div className="flex items-center gap-2 sm:gap-4">
          <span>TARGET: $1.0M SEED</span>
          <span className="text-slate-300 dark:text-slate-700">•</span>
          <span>VALUATION CAP: $8.0M SAFE</span>
        </div>
        {onNext && (
          <button
            onClick={onNext}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gold-500 hover:bg-gold-400 text-navy-950 font-bold transition-all shadow-sm min-h-[36px]"
          >
            <span>Start Presentation</span>
            <ArrowRight className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
    </div>
  );
};
